/**
 * TOTP Session Management Utility
 * 
 * This utility manages TOTP verification state across browser tabs using localStorage.
 * sessionStorage is tab-specific and doesn't persist across tabs, which causes users
 * to verify TOTP on every new tab.
 */

const TOTP_VERIFIED_KEY = 'totpVerified';
const TOTP_VERIFIED_TIMESTAMP_KEY = 'totpVerifiedAt';
const TOTP_JUST_ENABLED_KEY = 'totpJustEnabled';
const POST_TOTP_REDIRECT_KEY = 'postTotpRedirect';
const TOTP_SETUP_AT_KEY = 'totpSetupAt';

// TOTP session expires after 24 hours (in milliseconds)
const TOTP_SESSION_DURATION = 24 * 60 * 60 * 1000;

/**
 * Check if TOTP is verified and the session is still valid
 */
export const isTOTPVerified = (): boolean => {
  const verified = localStorage.getItem(TOTP_VERIFIED_KEY) === 'true';
  const timestamp = localStorage.getItem(TOTP_VERIFIED_TIMESTAMP_KEY);

  if (!verified || !timestamp) {
    return false;
  }

  const verifiedAt = parseInt(timestamp, 10);
  const now = Date.now();
  const elapsed = now - verifiedAt;

  // Check if session has expired
  if (elapsed > TOTP_SESSION_DURATION) {
    console.log('🔒 TOTP session expired, clearing verification');
    clearTOTPSession();
    return false;
  }

  return true;
};

/**
 * Mark TOTP as verified for this session
 */
export const setTOTPVerified = (): void => {
  localStorage.setItem(TOTP_VERIFIED_KEY, 'true');
  localStorage.setItem(TOTP_VERIFIED_TIMESTAMP_KEY, Date.now().toString());
  console.log('🔐 TOTP marked as verified (localStorage, cross-tab)');
};

/**
 * Clear TOTP verification session
 */
export const clearTOTPSession = (): void => {
  localStorage.removeItem(TOTP_VERIFIED_KEY);
  localStorage.removeItem(TOTP_VERIFIED_TIMESTAMP_KEY);
  localStorage.removeItem(TOTP_JUST_ENABLED_KEY);
  localStorage.removeItem(POST_TOTP_REDIRECT_KEY);
  localStorage.removeItem(TOTP_SETUP_AT_KEY);
  console.log('🧹 TOTP session cleared');
};

/**
 * Set flag that TOTP was just enabled (to avoid bounce in dashboard guard)
 */
export const setTOTPJustEnabled = (): void => {
  localStorage.setItem(TOTP_JUST_ENABLED_KEY, 'true');
};

/**
 * Check if TOTP was just enabled
 */
export const wasTOTPJustEnabled = (): boolean => {
  return localStorage.getItem(TOTP_JUST_ENABLED_KEY) === 'true';
};

/**
 * Set flag for post-TOTP redirect (one-time bypass)
 */
export const setPostTOTPRedirect = (): void => {
  localStorage.setItem(POST_TOTP_REDIRECT_KEY, 'true');
};

/**
 * Check if post-TOTP redirect flag is set
 */
export const hasPostTOTPRedirect = (): boolean => {
  return localStorage.getItem(POST_TOTP_REDIRECT_KEY) === 'true';
};

/**
 * Clear post-TOTP redirect flag
 */
export const clearPostTOTPRedirect = (): void => {
  localStorage.removeItem(POST_TOTP_REDIRECT_KEY);
};

/**
 * Set TOTP setup timestamp
 */
export const setTOTPSetupTimestamp = (): void => {
  localStorage.setItem(TOTP_SETUP_AT_KEY, Date.now().toString());
};

/**
 * Get TOTP setup timestamp
 */
export const getTOTPSetupTimestamp = (): number | null => {
  const timestamp = localStorage.getItem(TOTP_SETUP_AT_KEY);
  return timestamp ? parseInt(timestamp, 10) : null;
};

/**
 * Get remaining time until TOTP session expires (in milliseconds)
 */
export const getRemainingSessionTime = (): number => {
  const timestamp = localStorage.getItem(TOTP_VERIFIED_TIMESTAMP_KEY);
  if (!timestamp) {
    return 0;
  }
  
  const verifiedAt = parseInt(timestamp, 10);
  const now = Date.now();
  const elapsed = now - verifiedAt;
  const remaining = TOTP_SESSION_DURATION - elapsed;
  
  return remaining > 0 ? remaining : 0;
};

